import { useEffect, useState } from 'react';

import {
  fetchCampaignEventsHistory,
  subscribeCampaignEvents,
  type ProgressEvent,
} from '@/lib/apis';

export function useCampaignEvents(campaignId: number | null | undefined): {
  events: ProgressEvent[];
  isLoading: boolean;
} {
  const [events, setEvents] = useState<ProgressEvent[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    if (campaignId == null) {
      setEvents([]);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    fetchCampaignEventsHistory(campaignId)
      .then((history) => {
        if (cancelled) return;
        setEvents(history);
        setIsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setIsLoading(false);
      });

    // The backend's stream replays history too. Dedupe by event identity so
    // those replays don't double-add to state.
    const seen = new Set<string>();
    const controller = subscribeCampaignEvents(campaignId, (event) => {
      if (cancelled) return;
      const key = `${event.stage}|${event.status}|${event.timestamp}|${event.funnel_stage ?? ''}|${event.ad_group_url ?? ''}`;
      if (seen.has(key)) return;
      seen.add(key);
      setEvents((prev) => {
        if (prev.length > 0 && seen.size === 1) {
          for (const existing of prev) {
            seen.add(
              `${existing.stage}|${existing.status}|${existing.timestamp}|${existing.funnel_stage ?? ''}|${existing.ad_group_url ?? ''}`,
            );
          }
          if (seen.has(key)) return prev;
        }
        return [...prev, event];
      });
    });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [campaignId]);

  return { events, isLoading };
}
